/**
 * Pizza Pass + Add-ons (sections/tea-pizza-pass-upsell.liquid)
 *
 * - Placement: the section renders at the bottom of the page; this moves the card into the product column right
 *   under the Build-Your-Own-Box block (else after the buy buttons, else it stays put). The popup is moved to
 *   <body> so no ancestor transform can trap its fixed positioning.
 * - Pizza Pass is one stylized check that mirrors the cart: on = exactly one Pizza Pass line, off = none.
 *   Checking adds to the cart immediately. It is never gated.
 * - Add-ons show "+ Add" until in the cart, then a stepper. Adding is refused unless the cart holds a pizza order
 *   (a Build-Your-Own line, or a product handle from the section settings). Any error Shopify returns while adding
 *   an add-on is shown as that same "needs a pizza order" message, since add-ons cannot be bought alone.
 * - Every cart change dispatches the theme's `cart:refresh` (as the bundle form does); the header count and cart
 *   drawer update through their own listeners. `cart:change` / `cart:refresh` from elsewhere re-sync this block.
 */
(function () {
  'use strict';

  var MOBILE = window.matchMedia('(max-width: 959px)');
  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var root = function () {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  };

  function fill(template, map) {
    return String(template || '').replace(/%(\w+)%/g, function (m, key) {
      return Object.prototype.hasOwnProperty.call(map, key) ? map[key] : m;
    });
  }

  function parseJSON(el, fallback) {
    try { return el ? JSON.parse(el.textContent) : fallback; } catch (e) { return fallback; }
  }

  /* ================================================================================================
     Placement
     ================================================================================================ */

  function dropStale(scope, el) {
    // Editor re-renders leave a previous copy behind: drop it before inserting the fresh one.
    var stale = scope.querySelector('[data-tea-pizza-pass][data-section-id="' + el.dataset.sectionId + '"]');
    if (stale && stale !== el) stale.parentNode.removeChild(stale);
  }

  function place(el) {
    if (el.dataset.tppPlaced) return;
    el.dataset.tppPlaced = '1';

    // 1. Product page: under the Build-Your-Own-Box block (else after the buy buttons).
    var info = document.querySelector('.product-info');
    if (info) {
      if (info.contains(el)) return;
      var anchor = info.querySelector('.product-info__block-item[data-block-type="flex-bundles-byob"]') ||
        info.querySelector('.product-info__block-item[data-block-type="buy-buttons"]');
      if (anchor) {
        dropStale(info, el);
        anchor.parentNode.insertBefore(el, anchor.nextSibling);
        return;
      }
    }

    // 2. Dynamic Pizza Builder page: full width of the pizza column, right below the "Tasty Add-Ons" group.
    var addons = document.querySelector('.pizza-selection .flavor-group.addons') ||
      document.querySelector('.pizza-selection .flavor-group:last-of-type');
    if (addons) {
      if (addons.parentNode.contains(el)) return;
      dropStale(addons.parentNode, el);
      el.classList.remove('product-info__block-item');
      el.classList.add('tpp--in-builder');
      addons.parentNode.insertBefore(el, addons.nextSibling);
      return;
    }

    // 3. Gameday Box builder: in the main column, right below the "extras" step (its add-on ticket row).
    var gdbMain = document.querySelector('.gdb-builder-main');
    if (gdbMain) {
      if (gdbMain.contains(el)) return;
      dropStale(gdbMain, el);
      el.classList.remove('product-info__block-item');
      el.classList.add('tpp--in-builder', 'tpp--in-gameday');
      var extras = gdbMain.querySelector('#gdb-extras');
      if (extras) gdbMain.insertBefore(el, extras.nextSibling);
      else gdbMain.appendChild(el);
    }
    // 4. Otherwise: stays where the section was placed.
  }

  /* ================================================================================================
     Block
     ================================================================================================ */

  function TeaPizzaPass(el) {
    var self = this;
    this.el = el;
    this.S = parseJSON(el.querySelector('[data-tpp-settings]'), {});
    this.passId = parseInt(this.S.passVariant, 10) || 0;
    this.passUnavailable = this.passId > 0 && this.S.passAvailable === false;
    this.handles = String(this.S.pizzaHandles || '').toLowerCase().split(',')
      .map(function (h) { return h.trim(); }).filter(Boolean);

    this.toggle = el.querySelector('[data-tpp-toggle]');
    this.sourceTag = String(this.S.sourceTag || '').trim();
    this.passMsg = el.querySelector('[data-tpp-pass-msg]');
    this.addonMsg = el.querySelector('[data-tpp-addons-msg]');

    this.addons = [];
    this.addonById = {};
    this.addonByBlock = {};
    Array.prototype.forEach.call(el.querySelectorAll('[data-tpp-addon]'), function (row) {
      var add = row.querySelector('[data-tpp-add]');
      var stepper = row.querySelector('[data-tpp-stepper]');
      if (!add || !stepper) return;
      var a = {
        row: row,
        id: parseInt(row.getAttribute('data-variant-id'), 10) || 0,
        data: parseJSON(row.querySelector('[data-tpp-addon-data]'), {}),
        hit: row.querySelector('[data-tpp-open]'),
        add: add,
        stepper: stepper,
        qtyEl: row.querySelector('[data-tpp-qty]'),
        dec: row.querySelector('[data-tpp-dec]'),
        inc: row.querySelector('[data-tpp-inc]'),
        qty: 0
      };
      self.addons.push(a);
      self.addonById[a.id] = a;
      if (row.dataset.blockId) self.addonByBlock[row.dataset.blockId] = a;
    });

    this.cart = null;
    this.hasPizza = false;
    this.busy = false;
    this.skipNextRefresh = false;
    this.timers = {};

    this.overlay = null;
    var ov = el.querySelector('[data-tpp-overlay]');
    if (ov && this.S.overlay && this.addons.length) this.overlay = new Overlay(this, ov);

    this.bind();
    this.refresh();
  }

  TeaPizzaPass.prototype.bind = function () {
    var self = this;

    if (this.toggle) {
      this.toggle.addEventListener('click', function () { self.onToggle(); });
    }

    this.addons.forEach(function (a) {
      a.add.addEventListener('click', function () { self.setAddon(a, +1, { fromList: true }); });
      a.inc.addEventListener('click', function () { self.setAddon(a, +1, {}); });
      a.dec.addEventListener('click', function () { self.setAddon(a, -1, {}); });
      if (self.overlay) {
        var isControl = function (target) { return !!target.closest('.tpp-addon__action, button:not([data-tpp-open]), a, input'); };
        a.row.addEventListener('pointerdown', function (e) { if (!isControl(e.target)) a.row.classList.add('is-pressed'); });
        ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (name) {
          a.row.addEventListener(name, function () { a.row.classList.remove('is-pressed'); });
        });
        a.row.addEventListener('click', function (e) {
          if (isControl(e.target)) return;
          e.preventDefault();
          self.overlay.open(a);
        });
      }
    });

    document.addEventListener('cart:change', function (e) {
      var cart = e && e.detail && e.detail.cart;
      if (cart && cart.items) self.apply(cart);
    });
    document.addEventListener('cart:refresh', function () {
      if (self.skipNextRefresh) { self.skipNextRefresh = false; return; }
      self.scheduleRefresh();
    });
    // The pizza builder pages fire this after their own adds.
    document.addEventListener('cart:updated', function () { self.scheduleRefresh(); });
    window.addEventListener('pageshow', function (e) { if (e.persisted) self.refresh(); });
  };

  TeaPizzaPass.prototype.scheduleRefresh = function () {
    var self = this;
    clearTimeout(this.timers.refresh);
    this.timers.refresh = setTimeout(function () { self.refresh(); }, 250);
  };

  TeaPizzaPass.prototype.fetchCart = function () {
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) {
        if (!r.ok) throw new Error('cart');
        return r.json();
      });
  };

  TeaPizzaPass.prototype.refresh = function () {
    var self = this;
    return this.fetchCart().then(function (cart) { self.apply(cart); }).catch(function () {});
  };

  TeaPizzaPass.prototype.request = function (path, body) {
    return fetch(root() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (data) {
        if (!r.ok) {
          var err = new Error(data.description || data.message || 'Cart error');
          err.status = r.status;
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  };

  /* ---------- cart -> UI ---------- */

  TeaPizzaPass.prototype.apply = function (cart) {
    var self = this;
    this.cart = cart;
    var items = cart.items || [];
    var passQty = 0;
    var pizza = false;
    var addonQty = {};

    items.forEach(function (item) {
      if (self.passId && item.variant_id === self.passId) {
        passQty += item.quantity;
      } else if (self.addonById[item.variant_id]) {
        addonQty[item.variant_id] = (addonQty[item.variant_id] || 0) + item.quantity;
      } else if (self.isPizza(item)) {
        pizza = true;
      }
    });

    this.hasPizza = pizza;
    this.setPassUI(passQty > 0);
    this.addons.forEach(function (a) {
      a.qty = addonQty[a.id] || 0;
      self.renderAddon(a);
    });
    if (this.overlay) this.overlay.sync();
  };

  TeaPizzaPass.prototype.isPizza = function (item) {
    var props = item.properties || {};
    if (props._components || props._pack_size) return true;
    var handle = String(item.handle || '').toLowerCase();
    return this.handles.indexOf(handle) !== -1;
  };

  TeaPizzaPass.prototype.setPassUI = function (on) {
    if (!this.toggle) return;
    this.toggle.classList.toggle('is-on', on);
    this.toggle.setAttribute('aria-pressed', on ? 'true' : 'false');
  };

  TeaPizzaPass.prototype.renderAddon = function (a) {
    var inCart = a.qty > 0;
    a.add.hidden = inCart;
    a.stepper.hidden = !inCart;
    if (a.qtyEl) a.qtyEl.textContent = String(a.qty);
  };

  /* ---------- UI -> cart ---------- */

  /** Line to send to cart/add.js, stamped with the hidden _source property when a source tag is set. */
  TeaPizzaPass.prototype.lineFor = function (id, qty) {
    var line = { id: id, quantity: qty };
    if (this.sourceTag) line.properties = { _source: this.sourceTag };
    return line;
  };

  TeaPizzaPass.prototype.onToggle = function () {
    var self = this;
    if (!this.toggle || this.busy || this.passUnavailable || !this.passId) return;
    var want = !this.toggle.classList.contains('is-on');

    this.setBusy(true, this.toggle);
    this.setPassUI(want);

    this.fetchCart()
      .then(function (cart) {
        var lines = (cart.items || []).filter(function (i) { return i.variant_id === self.passId; });
        var chain = Promise.resolve();
        if (want) {
          if (lines.length === 0) {
            return self.request('cart/add.js', { items: [self.lineFor(self.passId, 1)] });
          }
          lines.forEach(function (line, idx) {
            var qty = idx === 0 ? 1 : 0;
            if (line.quantity !== qty) {
              chain = chain.then(function () { return self.request('cart/change.js', { id: line.key, quantity: qty }); });
            }
          });
          return chain;
        }
        lines.forEach(function (line) {
          chain = chain.then(function () { return self.request('cart/change.js', { id: line.key, quantity: 0 }); });
        });
        return chain;
      })
      .then(function () { return self.afterChange(); })
      .catch(function () {
        self.setPassUI(!want);
        self.flash(self.passMsg, self.S.errorMessage, true);
      })
      .then(function () { self.setBusy(false, self.toggle); });
  };

  /**
   * delta: +1 / -1, applied to the quantity read from a fresh cart fetch (never to a possibly stale local count).
   * opts.fromList  - pressed "+ Add" in the list (may open the cart drawer)
   * opts.fromOverlay - pressed the popup stepper (errors show inside the popup, drawer never opens)
   */
  TeaPizzaPass.prototype.setAddon = function (a, delta, opts) {
    var self = this;
    opts = opts || {};
    if (this.busy) return Promise.resolve();

    var target = opts.fromOverlay ? (this.overlay && this.overlay.el.stepper) : (opts.fromList ? a.add : a.stepper);
    this.setBusy(true, target);

    var refuse = function (text) {
      if (opts.fromOverlay && self.overlay) self.overlay.note(text);
      else self.flash(self.addonMsg, text, true);
      self.shake(a.row);
    };

    return this.fetchCart()
      .then(function (cart) {
        self.apply(cart);
        var qty = Math.max(0, a.qty + delta);

        if (qty > a.qty && !self.hasPizza) {
          refuse(self.S.gateMessage);
          return null;
        }

        var lines = (cart.items || []).filter(function (i) { return i.variant_id === a.id; });
        var op;
        if (qty === 0 && lines.length) {
          op = Promise.resolve();
          lines.forEach(function (line) {
            op = op.then(function () { return self.request('cart/change.js', { id: line.key, quantity: 0 }); });
          });
        } else if (lines.length) {
          // The same add-on may sit on two lines (e.g. one from the cart drawer upsell): apply the delta to the first.
          var first = lines[0];
          op = self.request('cart/change.js', { id: first.key, quantity: Math.max(0, first.quantity + (qty - a.qty)) });
        } else if (qty > 0) {
          op = self.request('cart/add.js', { items: [self.lineFor(a.id, qty)] });
        } else {
          return null;
        }

        return op.then(function () { return self.afterChange(); }).then(function () {
          if (opts.fromList && self.S.openCart) self.openDrawer();
        });
      })
      .catch(function (err) {
        // Shopify's cart validation refuses add-ons without a pizza pack: say that, not the raw error.
        var fromShopify = err && err.status;
        refuse(fromShopify ? self.S.gateMessage : self.S.errorMessage);
        if (window.console && fromShopify) console.warn('[Pizza Pass] cart refused add-on:', err.message);
      })
      .then(function () { self.setBusy(false, target); });
  };

  TeaPizzaPass.prototype.afterChange = function () {
    var self = this;
    return this.fetchCart().then(function (cart) {
      self.apply(cart);
      self.skipNextRefresh = true;
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    });
  };

  TeaPizzaPass.prototype.openDrawer = function () {
    // Pizza builder pages own a custom drawer and expose this; otherwise use the theme's drawer.
    if (typeof window.triggerDrawerLogic === 'function') {
      try { window.triggerDrawerLogic(); } catch (e) { /* builder not ready; cart still updated */ }
      return;
    }
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.show === 'function') {
      try { drawer.show(); } catch (e) { /* drawer not ready; cart still updated */ }
    }
  };

  /* ---------- feedback ---------- */

  TeaPizzaPass.prototype.setBusy = function (busy, target) {
    this.busy = busy;
    this.el.classList.toggle('is-busy', busy);
    if (target) target.classList.toggle('is-loading', busy);
    if (this.toggle) this.toggle.disabled = busy || this.passUnavailable;
    this.addons.forEach(function (a) {
      a.add.disabled = busy;
      a.inc.disabled = busy;
      a.dec.disabled = busy;
    });
    if (this.overlay) this.overlay.sync();
  };

  TeaPizzaPass.prototype.flash = function (el, text, isError) {
    if (!el) return;
    var key = el === this.passMsg ? 'pass' : 'addon';
    el.textContent = text;
    el.classList.toggle('is-error', !!isError);
    el.hidden = false;
    clearTimeout(this.timers[key]);
    this.timers[key] = setTimeout(function () { el.hidden = true; }, isError ? 5000 : 2500);
    if (isError && typeof el.scrollIntoView === 'function') {
      try { el.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch (e) { /* older WebKit */ }
    }
  };

  TeaPizzaPass.prototype.shake = function (row) {
    if (!row) return;
    row.classList.remove('is-shake');
    void row.offsetWidth;
    row.classList.add('is-shake');
    setTimeout(function () { row.classList.remove('is-shake'); }, 450);
  };

  /* ================================================================================================
     Add-on popup (same behaviour as the pizza popup: bottom sheet on phones, centered card on desktop)
     ================================================================================================ */

  function Overlay(block, rootEl) {
    var self = this;
    this.block = block;
    this.root = rootEl;
    this.current = null;
    this.lastFocus = null;
    this.openedByKeyboard = false;

    // Fixed positioning must not sit under a transformed ancestor: keep the popup on <body>.
    var old = document.body.querySelector('[data-tpp-overlay][data-section-id="' + rootEl.dataset.sectionId + '"]');
    if (old && old !== rootEl) old.parentNode.removeChild(old);
    document.body.appendChild(rootEl);

    var q = function (sel) { return rootEl.querySelector(sel); };
    this.el = {
      veil: q('[data-tpp-ov-veil]'),
      sheet: q('[data-tpp-ov-sheet]'),
      body: q('[data-tpp-ov-body]'),
      gallery: q('[data-tpp-ov-gallery]'),
      track: q('[data-tpp-ov-track]'),
      dots: q('[data-tpp-ov-dots]'),
      prev: q('[data-tpp-ov-prev]'),
      next: q('[data-tpp-ov-next]'),
      chip: q('[data-tpp-ov-chip]'),
      title: q('[data-tpp-ov-title]'),
      price: q('[data-tpp-ov-price]'),
      desc: q('[data-tpp-ov-desc]'),
      stepper: rootEl.querySelector('.tpp-ov__stepper'),
      stepperLabel: q('[data-tpp-ov-stepper-label]'),
      qty: q('[data-tpp-ov-qty]'),
      minus: q('[data-tpp-ov-minus]'),
      plus: q('[data-tpp-ov-plus]'),
      note: q('[data-tpp-ov-note]'),
      done: q('[data-tpp-ov-done]'),
      close: q('[data-tpp-ov-close]')
    };

    var el = this.el;
    el.close.addEventListener('click', function () { self.close(); });
    el.done.addEventListener('click', function () { self.close(); });
    el.veil.addEventListener('click', function () { self.close(); });
    el.minus.addEventListener('click', function () { if (self.current) block.setAddon(self.current, -1, { fromOverlay: true }); });
    el.plus.addEventListener('click', function () { if (self.current) block.setAddon(self.current, +1, { fromOverlay: true }); });

    el.track.addEventListener('scroll', function () { self.setActiveDot(self.slideIndex()); }, { passive: true });
    el.dots.addEventListener('click', function (e) {
      var dot = e.target.closest('[data-tpp-ov-dot]');
      if (dot) self.goTo(parseInt(dot.dataset.tppOvDot, 10));
    });
    el.prev.addEventListener('click', function () { self.goTo(self.slideIndex() - 1); });
    el.next.addEventListener('click', function () { self.goTo(self.slideIndex() + 1); });

    document.addEventListener('keydown', function (e) {
      if (!self.current) return;
      if (e.key === 'Escape') { e.preventDefault(); self.close(); return; }
      if (e.key === 'ArrowLeft') { self.goTo(self.slideIndex() - 1); return; }
      if (e.key === 'ArrowRight') { self.goTo(self.slideIndex() + 1); return; }
      if (e.key === 'Tab') {
        var focusable = Array.prototype.filter.call(el.sheet.querySelectorAll(FOCUSABLE), function (n) { return n.offsetParent !== null; });
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });

    block.addons.forEach(function (a) {
      if (!a.hit) return;
      a.hit.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { self.openedByKeyboard = true; }
      });
    });

    if (window.Shopify && Shopify.designMode) {
      document.addEventListener('shopify:block:select', function (e) {
        var row = e.target && e.target.closest ? e.target.closest('[data-tpp-addon]') : null;
        if (!row || !block.el.contains(row)) return;
        var a = block.addonByBlock[row.dataset.blockId];
        if (a) self.open(a);
      });
      document.addEventListener('shopify:block:deselect', function (e) {
        var row = e.target && e.target.closest ? e.target.closest('[data-tpp-addon]') : null;
        if (row && block.el.contains(row)) self.close();
      });
    }

    this.bindDrag();
  }

  Overlay.prototype.open = function (a) {
    var el = this.el;
    var d = a.data || {};
    var self = this;

    this.block.addons.forEach(function (other) { if (other !== a) other.row.classList.remove('is-open'); });
    this.current = a;

    this.renderGallery(d.images || [], d.title || '');
    el.chip.textContent = d.chip || '';
    el.chip.hidden = !d.chip;
    el.title.textContent = d.popupTitle || d.title || '';
    el.price.textContent = d.price || '';
    el.desc.innerHTML = d.desc || '';
    el.desc.hidden = !d.desc;
    el.note.hidden = true;
    this.sync();

    a.row.classList.add('is-open');
    this.root.classList.add('is-open');
    el.sheet.setAttribute('aria-hidden', 'false');
    el.sheet.style.transform = '';
    document.documentElement.classList.add('tpp-scroll-lock');

    this.lastFocus = document.activeElement;
    el.body.scrollTop = 0;
    this.goTo(0, false);
    try { el.sheet.focus({ preventScroll: true }); } catch (err) { el.sheet.focus(); }
    setTimeout(function () { self.openedByKeyboard = false; }, 0);
  };

  Overlay.prototype.close = function () {
    if (!this.current) return;
    var el = this.el;
    var a = this.current;
    this.root.classList.remove('is-open');
    el.sheet.setAttribute('aria-hidden', 'true');
    el.sheet.style.transform = '';
    el.sheet.style.transition = '';
    document.documentElement.classList.remove('tpp-scroll-lock');
    a.row.classList.remove('is-open');
    this.current = null;
    var focusTarget = this.openedByKeyboard ? (a.hit || this.lastFocus) : null;
    if (focusTarget && typeof focusTarget.focus === 'function') {
      try { focusTarget.focus({ preventScroll: true }); } catch (err) { /* ignore */ }
    } else if (document.activeElement && el.sheet.contains(document.activeElement)) {
      document.activeElement.blur();
    }
  };

  Overlay.prototype.sync = function () {
    if (!this.current) return;
    var el = this.el;
    var S = this.block.S;
    var qty = this.current.qty;
    el.qty.textContent = String(qty);
    el.stepperLabel.textContent = qty > 0 ? fill(S.overlayInOrderLabel, { n: qty }) : (S.overlayAddLabel || '');
    el.minus.disabled = this.block.busy || qty <= 0;
    el.plus.disabled = this.block.busy;
    el.stepper.classList.toggle('is-loading', this.block.busy);
  };

  Overlay.prototype.note = function (text) {
    var el = this.el;
    var self = this;
    el.note.textContent = text || '';
    el.note.hidden = !text;
    clearTimeout(this.noteTimer);
    this.noteTimer = setTimeout(function () { el.note.hidden = true; }, 5000);
  };

  Overlay.prototype.renderGallery = function (images, title) {
    var el = this.el;
    var safeTitle = String(title).replace(/"/g, '&quot;');
    el.track.innerHTML = images.map(function (image, i) {
      var src = typeof image === 'string' ? image : image.src;
      return '<div class="tpp-ov__slide"><img src="' + src + '" alt="' + safeTitle + '"' + (i ? ' loading="lazy"' : '') + ' draggable="false"></div>';
    }).join('');
    el.dots.innerHTML = images.length > 1 ? images.map(function (_, i) {
      return '<button type="button" class="' + (i === 0 ? 'is-active' : '') + '" aria-label="Photo ' + (i + 1) + '" data-tpp-ov-dot="' + i + '"></button>';
    }).join('') : '';
    el.gallery.hidden = images.length === 0;
    el.gallery.classList.toggle('is-single', images.length < 2);
    el.track.scrollLeft = 0;
  };

  Overlay.prototype.slideIndex = function () {
    var track = this.el.track;
    return Math.round(track.scrollLeft / (track.clientWidth || 1));
  };

  Overlay.prototype.goTo = function (index, smooth) {
    var track = this.el.track;
    var count = track.children.length;
    if (!count) return;
    index = Math.max(0, Math.min(count - 1, index));
    var left = index * track.clientWidth;
    if (smooth !== false && !REDUCED_MOTION.matches) track.scrollTo({ left: left, behavior: 'smooth' });
    else track.scrollLeft = left;
    this.setActiveDot(index);
  };

  Overlay.prototype.setActiveDot = function (index) {
    Array.prototype.forEach.call(this.el.dots.children, function (dot, i) { dot.classList.toggle('is-active', i === index); });
  };

  Overlay.prototype.bindDrag = function () {
    var self = this;
    var el = this.el;
    var drag = null;

    el.sheet.addEventListener('touchstart', function (e) {
      if (!MOBILE.matches || !self.current || e.touches.length !== 1) return;
      var t = e.touches[0];
      drag = { x: t.clientX, y: t.clientY, t: Date.now(), active: false, cur: 0, fromGrab: !!e.target.closest('[data-tpp-ov-grab]') };
    }, { passive: true });

    el.sheet.addEventListener('touchmove', function (e) {
      if (!drag || e.touches.length !== 1) return;
      var t = e.touches[0];
      var dx = t.clientX - drag.x;
      var dy = t.clientY - drag.y;
      if (!drag.active) {
        if (Math.abs(dy) < 8 && Math.abs(dx) < 8) return;
        var vertical = Math.abs(dy) > Math.abs(dx) * 1.2;
        if (!(drag.fromGrab || (vertical && dy > 0 && el.body.scrollTop <= 0))) { drag = null; return; }
        drag.active = true;
        el.sheet.style.transition = 'none';
      }
      drag.cur = Math.max(0, dy);
      el.sheet.style.transform = 'translateY(' + drag.cur + 'px)';
      if (e.cancelable) e.preventDefault();
    }, { passive: false });

    var end = function () {
      if (!drag) return;
      var d = drag;
      drag = null;
      if (!d.active) return;
      el.sheet.style.transition = '';
      if (d.cur > 110 || (d.cur > 40 && (Date.now() - d.t) < 250)) self.close();
      else el.sheet.style.transform = '';
    };
    el.sheet.addEventListener('touchend', end);
    el.sheet.addEventListener('touchcancel', end);
  };

  /* ================================================================================================
     Boot
     ================================================================================================ */

  function init(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-tea-pizza-pass]'), function (el) {
      if (el.teaPizzaPass) return;
      place(el);
      el.teaPizzaPass = new TeaPizzaPass(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); }, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var id = e.detail && e.detail.sectionId;
    if (!id) return;
    var moved = document.querySelector('[data-tea-pizza-pass][data-section-id="' + id + '"]');
    if (moved && moved.teaPizzaPass && moved.teaPizzaPass.overlay) moved.teaPizzaPass.overlay.close();
    if (moved && moved.parentNode) moved.parentNode.removeChild(moved);
    var ov = document.body.querySelector('[data-tpp-overlay][data-section-id="' + id + '"]');
    if (ov && ov.parentNode) ov.parentNode.removeChild(ov);
    document.documentElement.classList.remove('tpp-scroll-lock');
  });
})();
